import { useCallback, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { webAuthnService, type Passkey } from 'app/services/webauthn.service';
import { clearPasskeyCreated, clearPasskeyPromptSuppression } from 'app/auth/passkeyPromptChoice';
import { clearPasskeyPromptShownThisSession } from 'app/auth/passkeyPromptSession';
import { useAppSelector } from 'app/store';
import { selectAuth } from 'app/store/authSlice';

/**
 * SEE-226: the caller's registered passkeys, plus removal. Shared by the web
 * Security tab and the mobile Security page so both surfaces stay in step.
 *
 * Failures are exposed as flags rather than messages: the transport layer's
 * errors are not user-facing copy, and each surface renders its own translated
 * text (same split as useCreatePasskey / PasskeyPrompt).
 */
export interface UsePasskeysResult {
  passkeys: Passkey[];
  /** True during the initial load and any reload. */
  loading: boolean;
  /** The list could not be fetched — the surface shows a retryable error. */
  loadFailed: boolean;
  /** credentialId currently being removed, or null. */
  removing: string | null;
  /** The last removal failed — cleared when another one starts. */
  removeFailed: boolean;
  reload: () => Promise<void>;
  /** Removes a passkey and refreshes the list. Resolves true on success. */
  remove: (credentialId: string) => Promise<boolean>;
}

export function usePasskeys(): UsePasskeysResult {
  const { user } = useAppSelector(selectAuth);
  const sub = user?.sub;
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [removeFailed, setRemoveFailed] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const list = await webAuthnService.listPasskeys();
      setPasskeys(list);
      // Self-heal a stale marker: the server is authoritative, so an empty list
      // means no device should still be claiming "this user has a passkey". This
      // catches the case removal can't — the marker left behind on a DIFFERENT
      // device when the passkey was deleted elsewhere.
      //
      // Only the empty direction is safe to sync. A non-empty list does NOT imply
      // the credentials are usable on THIS device (a device-bound passkey lives on
      // the machine that made it), so setting the marker from it would wrongly
      // suppress a prompt this device should still see.
      if (list.length === 0 && sub) {
        clearPasskeyCreated(sub);
      }
    } catch (e) {
      // webAuthnService already reports to Sentry; keep the list as-is so a
      // failed reload doesn't blank out passkeys the user could still remove.
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [sub]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const remove = useCallback(
    async (credentialId: string): Promise<boolean> => {
      setRemoving(credentialId);
      setRemoveFailed(false);
      try {
        const success = await webAuthnService.deletePasskey(credentialId);
        if (!success) {
          setRemoveFailed(true);
          return false;
        }
        // Drop the row locally so the list settles immediately (deletePasskey is
        // keyed, so it can't be a partial).
        const remaining = passkeys.filter((p) => p.credentialId !== credentialId);
        setPasskeys(remaining);
        // Removing the LAST passkey must undo the "user has a passkey" marker
        // written at registration, or the post-login prompt stays suppressed on
        // this device forever while the server says they have none. The
        // session-scoped "already shown" guard is cleared too, so deleting and
        // signing back in within the same tab still re-offers a passkey.
        if (remaining.length === 0 && sub) {
          clearPasskeyPromptSuppression(sub);
          clearPasskeyPromptShownThisSession(sub);
        }
        return true;
      } catch (e) {
        captureException(e, { tags: { area: 'webauthn', flow: 'delete' } });
        setRemoveFailed(true);
        return false;
      } finally {
        setRemoving(null);
      }
    },
    [passkeys, sub]
  );

  return { passkeys, loading, loadFailed, removing, removeFailed, reload, remove };
}

export default usePasskeys;
